import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { Big } from 'big.js';
import * as _ from 'lodash';
import { PledgeProductRead } from '../../definition/interface/Pledge';
import { FormConfig } from '../../definition/types/subscription';
import FormGroupArray from '../../services/FormGroupArray';

// Arrondit un pourcentage affiché à 3 décimales (évite les artefacts de calcul flottant, ex.
// 33.333333333333336 → 33.333). Big plutôt que Math.round : évite aussi le cas classique où
// l'étape intermédiaire *1000 est elle-même imprécise en flottant (ex. 1.005*1000) et fait arrondir
// dans le mauvais sens.
function roundPercent(value: number): number {
  return new Big(value).round(3).toNumber();
}
@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  options: any = {};

  generateForm(entity: PledgeProductRead & { parameters: any }, options) {
    this.options = { ...options, ...entity };

    let supportRepartition: UntypedFormGroup[] = [];

    const supportRepartitionIsin = entity?.parameters?.supportRepartition;

    if (supportRepartitionIsin)
      _.forEach(supportRepartitionIsin, (scpi, i) =>
        supportRepartition.push(
          this.formBuilder.group(this.createModel(scpi, i)),
        ),
      );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        productPackId: [entity?.parameters?.productPackId],
        supportRepartition: this.formBuilder.array(
          supportRepartition,
          Validators.nullValidator,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, {});
  }
  generate(entity: any[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }
  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }
  public createModel(item, index) {
    let { initalRepartitionInitial, monthlyRepartitionInitial } =
      this.getPercentValueInitial(index);

    let { initalRepartitionInitialAmount, monthlyRepartitionInitialAmount } =
      this.getAmountValueInitial(index);

    const InitalRepartitionValue = this.getInitalRepartition(
      _.cloneDeep(item),
      _.cloneDeep(initalRepartitionInitial),
      _.cloneDeep(initalRepartitionInitialAmount),
    );
    const MonthlyRepartitionValue = this.getMonthlyRepartition(
      _.cloneDeep(item),
      _.cloneDeep(monthlyRepartitionInitial),
      _.cloneDeep(monthlyRepartitionInitialAmount),
    );

    let model = {
      id: [item ? item.id : null],
      isin: [item ? item.isin || item.supportIsin : null],
      index: [item ? item.index : null],
      omniprodId: [item ? item.omniProductId || item.omniprodId : null],
      name: [item ? item.name : null],

      initalRepartition: [InitalRepartitionValue],
      monthlyRepartition: [MonthlyRepartitionValue],
      favorite: [item ? item.favorite : null],
      type: [item ? item.type : null],
      supportDescription: [
        this.options ? this.options.supportDescription : null,
      ],
      totalAvInitialPayment: [
        this.options?.parameters ? +this.options.parameters.avInitialPayment : null,
      ],
      totalAvMonthlyPayment: [
        this.options?.parameters ? +this.options.parameters.avMonthlyPayment : null,
      ],
      companyUpfront: [item?.companyUpfront ?? null],
      emitterUpfront: [item?.emitterUpfront ?? null],
      riskScore: [item?.riskScore ?? null],
      supportRepartitionType: [item?.supportRepartitionType ?? 'PERCENTAGE'],
      // PRE-4896 : cette slide n'affiche ni ne modifie ces 8 champs, mais doit les faire transiter
      // tels quels (préremplis par la commande COMPLETE_SUPPORT_RATES à la slide précédente) —
      // sans cela, l'extract() de cette slide écraserait leur valeur à null au submit, avant même
      // que la slide "tauxParSupport" ait pu les afficher.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      upfrontAssureurSaisi: [item?.upfrontAssureurSaisi ?? null],
      upfrontEmetteurSaisi: [item?.upfrontEmetteurSaisi ?? null],
      fraisGestionSaisi: [item?.fraisGestionSaisi ?? null],
      retrocessionAssureurSaisi: [item?.retrocessionAssureurSaisi ?? null],
    };

    return model;
  }
  private getMonthlyRepartition(
    item,
    monthlyRepartitionInitial,
    monthlyRepartitionAmount,
  ) {
    if (item?.supportRepartitionType == 'AMOUNT') {
      return item?.monthlyRepartition
        ? +item?.monthlyRepartition
        : monthlyRepartitionAmount || 0;
    }
    return item?.monthlyRepartition
      ? roundPercent(+item.monthlyRepartition * 100)
      : monthlyRepartitionInitial || 0;
  }
  private getInitalRepartition(
    item,
    initalRepartitionInitial,
    initalRepartitionAmount,
  ) {
    if (item?.supportRepartitionType == 'AMOUNT') {
      return item?.initalRepartition
        ? +item?.initalRepartition
        : initalRepartitionAmount || 0;
    }
    return item?.initalRepartition
      ? roundPercent(+item.initalRepartition * 100)
      : initalRepartitionInitial || 0;
  }
  getPercentValueInitial(index: any) {
    const copyOptions = _.cloneDeep(this.options);

    const rest = 100 % copyOptions?.parameters?.supportRepartition?.length;

    const sumInitalRepartition =
      _.sumBy(copyOptions?.parameters?.supportRepartition, (o: any) => {
        return o?.initalRepartition * 100 || 0;
      }) || 0;
    const repartitionInitalValue =
      (100 - sumInitalRepartition) /
        copyOptions?.parameters?.supportRepartition?.length || 0;

    let initalRepartitionInitial = repartitionInitalValue.toFixed() as any;
    if (rest != 0) {
      initalRepartitionInitial = Math.ceil(repartitionInitalValue) - 1;
    }
    if (
      rest != 0 &&
      index === copyOptions?.parameters?.supportRepartition?.length - 1
    ) {
      initalRepartitionInitial = Math.ceil(repartitionInitalValue) - 1 + rest;
    }

    const sumMonthlyRepartition =
      _.sumBy(copyOptions?.parameters?.supportRepartition, (o: any) => {
        return o?.monthlyRepartition * 100 || 0;
      }) || 0;
    const repartitionMonthlyValue =
      (100 - sumMonthlyRepartition) /
        copyOptions?.parameters?.supportRepartition?.length || 0;

    let monthlyRepartitionInitial = repartitionMonthlyValue.toFixed() as any;
    if (rest != 0) {
      monthlyRepartitionInitial = Math.ceil(repartitionMonthlyValue) - 1;
    }
    if (
      rest != 0 &&
      index === copyOptions?.parameters?.supportRepartition?.length - 1
    ) {
      monthlyRepartitionInitial = Math.ceil(repartitionMonthlyValue) - 1 + rest;
    }
    return {
      initalRepartitionInitial,
      monthlyRepartitionInitial,
    };
  }
  getAmountValueInitial(index: number) {
    const copyOptions = _.cloneDeep(this.options);
    const payment = +copyOptions?.parameters?.avMonthlyPayment || 0;

    const length = copyOptions?.parameters?.supportRepartition?.length || 1; // minimum length is 1 to avoid division by zero

    const amountPerSupport = payment / length;

    // Calculate the amount based on the index
    const rest = payment % length; // remaining amount after dividing equally
    let amount = amountPerSupport;

    if (length === 0)
      return {
        initalRepartitionInitialAmount: 0,
        monthlyRepartitionInitialAmount: 0,
      }; // Handling for edge case

    if (rest !== 0 && index < rest) {
      amount = amountPerSupport + 1; // Add 1 to amount for indexes that receive the remaining amount
    }
    const initalRepartitionInitialAmount = amount,
      monthlyRepartitionInitialAmount = amount;
    return {
      initalRepartitionInitialAmount,
      monthlyRepartitionInitialAmount,
    };
  }

  extract() {
    const formValue =
      this.form.value?.products?.[0]?.parameters?.supportRepartition;
    const supportRepartition = _.filter(formValue, o => {
      return o.isin != undefined || o.isin != null;
    });

    // pick only isin
    const supportRepartitionIsin = _.map(supportRepartition, o => {
      return {
        isin: o.isin,
        name: o.name,
        initalRepartition:
          o.supportRepartitionType == 'AMOUNT'
            ? o.initalRepartition
            : o.initalRepartition
              ? o.initalRepartition / 100
              : 0,
        monthlyRepartition:
          o.supportRepartitionType == 'AMOUNT'
            ? o.monthlyRepartition
            : o.monthlyRepartition
              ? o.monthlyRepartition / 100
              : 0,
        type: o.type,
        riskScore: o.riskScore,
        supportRepartitionType: o?.supportRepartitionType ?? 'PERCENTAGE',
        // PRE-4896 : repassés tels quels, cf. commentaire dans createModel() ci-dessus.
        upfrontAssureurAutocomplete: o.upfrontAssureurAutocomplete ?? null,
        upfrontEmetteurAutocomplete: o.upfrontEmetteurAutocomplete ?? null,
        fraisGestionAutocomplete: o.fraisGestionAutocomplete ?? null,
        retrocessionAssureurAutocomplete:
          o.retrocessionAssureurAutocomplete ?? null,
        upfrontAssureurSaisi: o.upfrontAssureurSaisi ?? null,
        upfrontEmetteurSaisi: o.upfrontEmetteurSaisi ?? null,
        fraisGestionSaisi: o.fraisGestionSaisi ?? null,
        retrocessionAssureurSaisi: o.retrocessionAssureurSaisi ?? null,
      };
    });

    const products = this.form.value?.products;
    const productsIsin = _.map(products, o => {
      return {
        ...o,
        parameters: {
          ...o.parameters,
          supportRepartition: supportRepartitionIsin,
        },
      };
    });

    return productsIsin;
  }
}
